import React from "react";
import { motion } from "framer-motion";
import { Award, Target, Eye, Sparkles, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTABand from "@/components/CTABand";
import { Reveal } from "@/lib/motion"; 
import { IMAGES } from "@/lib/content";
import VastuCompass from "@/components/VastuCompass";

const JOURNEY = [
  { year: "The Beginning", text: "Komel's fascination with the invisible forces shaping human life began early — a curiosity that grew into a rigorous, lifelong study of Vastu, astrology and energy science." },
  { year: "The Science", text: "Rejecting blind superstition, she developed a measurable, evidence-led methodology — mapping directional energy and auras with precision and logic." },
  { year: "Dubai & Beyond", text: "Today she serves discerning clients across Dubai and the UAE — from luxury villas and corporate towers to sprawling industrial units." },
];

export default function About() {
  return (
    <div className="relative z-10">
      <PageHeader
        overline="About the Consultant"
        titleLines={["Komel Valliea", <span key="2" className="text-gold italic">Scientific Vastu Expert</span>]}
        subtitle="Blending spirituality with science to align spaces, businesses and lives with prosperity and positive energy."
        image={IMAGES.skyline2}
      />

      <section className="py-16 md:py-24 relative overflow-hidden">
        <VastuCompass className="absolute -left-52 top-40 w-[560px] h-[560px] opacity-[0.10] pointer-events-none hidden lg:block" />
        <div className="mx-auto max-w-7xl px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start relative">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal y={50}>
              <div className="relative">
                <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-primary-green/20 to-gold/10 blur-2xl" />
                <div className="absolute -top-5 -left-5 h-24 w-24 border-l-2 border-t-2 border-gold/60 rounded-tl-3xl" />
                <img src="/profile.png" alt="Komel Valliea" className="relative rounded-[1.75rem] w-full object-cover shadow-2xl" data-testid="about-photo" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline text-gold mb-5">Introduction</p>
              <h2 className="font-display text-3xl md:text-4xl leading-tight">A modern approach to an ancient science</h2>
              <p className="mt-6 text-[#94A3B8] text-lg leading-relaxed">
                Komel Valliea is a distinguished Scientific Vastu consultant, astrologer, numerologist and energy expert based in Dubai. Her work is defined by a rare balance — deep spiritual intuition grounded in a rigorous, scientific framework. She has guided high-net-worth individuals, corporate leaders, builders, NRIs and industrialists towards spaces that radiate harmony, health and growth.
              </p>
              <p className="mt-4 text-[#94A3B8] text-lg leading-relaxed">
                Her philosophy is simple yet powerful: energy is real, measurable and correctable — without demolition, without fear, and always with logic.
              </p>
            </Reveal>

            <div className="mt-14 space-y-8">
              <Reveal><h3 className="font-display text-2xl text-gold">The Journey</h3></Reveal>
              {JOURNEY.map((j, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="border-l-2 border-white/10 pl-6 relative">
                    <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-gold" />
                    <h4 className="font-display text-xl mb-2">{j.year}</h4>
                    <p className="text-[#94A3B8] leading-relaxed">{j.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { icon: Target, t: "Mission", d: "To make the science of energy accessible, credible and transformative for every home and business." },
                { icon: Eye, t: "Vision", d: "A world where every space is aligned to nurture prosperity, health and peace." },
              ].map((m) => (
                <Reveal key={m.t}>
                  <div className="glass rounded-2xl p-7 h-full">
                    <m.icon className="text-primary-green mb-4" size={26} />
                    <h4 className="font-display text-2xl mb-2">{m.t}</h4>
                    <p className="text-[#94A3B8] leading-relaxed">{m.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <div className="mt-12 rounded-2xl border border-gold/20 bg-gold/[0.04] p-8">
                <h3 className="font-display text-2xl text-gold flex items-center gap-3"><Sparkles size={22} /> Why Scientific Vastu Works</h3>
                <ul className="mt-5 space-y-3">
                  {["Every recommendation is backed by measurable energy principles", "Corrections require no demolition or costly rebuilds", "A holistic ecosystem: Vastu, astrology, numerology & energy scanning", "Proven results across residential, commercial and industrial spaces"].map((p) => (
                    <li key={p} className="flex gap-3 text-[#CBD5E1]"><CheckCircle2 size={20} className="text-primary-green shrink-0 mt-0.5" /> {p}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
